import { useEffect, useState, type CSSProperties, type ReactNode } from "react";
import { showToast } from "../primitives";
import { mcpClient } from "../../../lib/mcp-client";
import { useUpdateWrappedApi, type WrappedApiSummary } from "../../hooks/use-wrapped-apis";

/**
 * Sprint 16b — owner-side OAuth client config for a wrapped API. The client
 * secret is write-only (never returned by the API), so it's only sent when the
 * owner enters a new value. The redirect URI to whitelist in the provider is
 * shown for copy. Authorization Code wrappers show the authorize URL field;
 * Client Credentials wrappers hide it (no user-facing authorize step).
 */
export function OAuthConfigSection({ wrapper, isOwner }: { wrapper: WrappedApiSummary; isOwner: boolean }) {
  const update = useUpdateWrappedApi();
  const isAuthCode = wrapper.auth_kind === "oauth2_auth_code";

  const [clientId, setClientId] = useState(wrapper.oauth_client_id ?? "");
  const [clientSecret, setClientSecret] = useState("");
  const [authorizeUrl, setAuthorizeUrl] = useState(wrapper.oauth_authorize_url ?? "");
  const [tokenUrl, setTokenUrl] = useState(wrapper.oauth_token_url ?? "");
  const [scopes, setScopes] = useState(wrapper.oauth_scopes ?? "");

  useEffect(() => {
    setClientId(wrapper.oauth_client_id ?? "");
    setAuthorizeUrl(wrapper.oauth_authorize_url ?? "");
    setTokenUrl(wrapper.oauth_token_url ?? "");
    setScopes(wrapper.oauth_scopes ?? "");
  }, [wrapper.slug, wrapper.oauth_client_id, wrapper.oauth_authorize_url, wrapper.oauth_token_url, wrapper.oauth_scopes]);

  const redirectUri = `${mcpClient.apiOrigin}/oauth/wrapped/${wrapper.slug}/callback`;

  function copyRedirect() {
    navigator.clipboard?.writeText(redirectUri).then(
      () => showToast("Redirect URI copied"),
      () => showToast("Couldn't copy", "error"),
    );
  }

  async function save() {
    if (!clientId.trim()) return showToast("Client ID is required.", "error", true);
    if (!tokenUrl.trim()) return showToast("Token URL is required.", "error", true);
    if (isAuthCode && !authorizeUrl.trim()) return showToast("Authorize URL is required.", "error", true);
    try {
      await update.mutateAsync({
        slug: wrapper.slug,
        oauth_client_id: clientId.trim(),
        ...(clientSecret.trim() ? { oauth_client_secret: clientSecret.trim() } : {}),
        ...(isAuthCode ? { oauth_authorize_url: authorizeUrl.trim() } : {}),
        oauth_token_url: tokenUrl.trim(),
        oauth_scopes: scopes.trim(),
      });
      showToast("OAuth config saved");
      setClientSecret("");
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Couldn't save OAuth config", "error", true);
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ fontSize: 12, color: "var(--color-text-secondary)", lineHeight: 1.5 }}>
        {isAuthCode
          ? "Authorization Code grant — each member authorises individually below."
          : "Client Credentials grant — no per-user step; calls use the client id + secret."}
      </div>

      <div>
        <span style={fieldLabel}>Redirect URI to whitelist in the provider</span>
        <div style={redirectBox}>
          <code style={{ fontSize: 11, fontFamily: "var(--font-mono)", wordBreak: "break-all", flex: 1 }}>{redirectUri}</code>
          <button type="button" onClick={copyRedirect} style={copyBtn} aria-label="Copy redirect URI">
            <i className="ti ti-copy" style={{ fontSize: 13 }} aria-hidden="true" />
          </button>
        </div>
      </div>

      {!isOwner ? (
        <div style={{ fontSize: 12, color: "var(--color-text-tertiary)" }}>
          {wrapper.oauth_configured ? "OAuth is configured by the owner." : "Awaiting OAuth setup by the owner."}
        </div>
      ) : (
        <>
          <Field label="Client ID">
            <input value={clientId} onChange={(e) => setClientId(e.target.value)} style={inputStyle} autoComplete="off" />
          </Field>
          <Field label={wrapper.oauth_configured ? "Client secret (leave blank to keep)" : "Client secret"}>
            <input
              type="password"
              value={clientSecret}
              onChange={(e) => setClientSecret(e.target.value)}
              placeholder={wrapper.oauth_configured ? "•••••• (unchanged)" : "paste secret"}
              style={inputStyle}
              autoComplete="off"
            />
          </Field>
          {isAuthCode && (
            <Field label="Authorize URL">
              <input value={authorizeUrl} onChange={(e) => setAuthorizeUrl(e.target.value)} placeholder="https://provider/oauth/authorize" style={inputStyle} />
            </Field>
          )}
          <Field label="Token URL">
            <input value={tokenUrl} onChange={(e) => setTokenUrl(e.target.value)} placeholder="https://provider/oauth/token" style={inputStyle} />
          </Field>
          <Field label="Scopes (space-separated)">
            <input value={scopes} onChange={(e) => setScopes(e.target.value)} placeholder="read write" style={inputStyle} />
          </Field>
          <div>
            <button type="button" onClick={save} disabled={update.isPending} style={saveBtn}>
              {update.isPending ? "Saving…" : "Save OAuth config"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span style={fieldLabel}>{label}</span>
      {children}
    </label>
  );
}

const fieldLabel: CSSProperties = { fontSize: 12, fontWeight: 500, color: "var(--color-text-secondary)" };

const inputStyle: CSSProperties = {
  width: "100%",
  boxSizing: "border-box",
  padding: "7px 10px",
  fontSize: 13,
  fontFamily: "var(--font-sans)",
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-sm)",
  background: "var(--color-background-primary)",
  color: "var(--color-text-primary)",
};

const redirectBox: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  background: "var(--color-background-secondary)",
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-sm)",
  padding: "6px 8px",
  marginTop: 4,
};

const copyBtn: CSSProperties = {
  border: "none",
  background: "none",
  color: "var(--color-text-secondary)",
  cursor: "pointer",
  padding: 2,
  flexShrink: 0,
};

const saveBtn: CSSProperties = {
  fontSize: 13,
  fontWeight: 500,
  padding: "7px 14px",
  borderRadius: "var(--border-radius-sm)",
  border: "none",
  background: "var(--color-text-primary)",
  color: "var(--color-background-primary)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
};
